<template>
  <v-treeview
    :items="items"
    :load-children="getChildren"
    activatable
    :active.sync="computedValue"
    :open.sync="open"
    return-object
    dense
    style="width: max-content"
    class="v-card__text pa-0"
  >
    <template #prepend="{ item, open }">
      <v-icon v-if="item._type === 'folder'">
        {{ open ? "mdi-folder-open" : "mdi-folder" }}
      </v-icon>
      <v-icon v-else> mdi-file-document-outline </v-icon>
    </template>
  </v-treeview>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "FolderTreeSelect",
  components: {},
  mixins: [],
  props: {
    value: {
      type: Object,
      required: false,
      default: () => ({ id: null, name: "Home", _type: "folder" }),
    },
    showCases: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectOnly: {
      type: String,
      required: false,
      default: "folder",
      validator: (v) => ["folder", "case"].includes(v),
    },
    returnObject: { type: Boolean, required: false, default: true },
    openedRoot: { type: Boolean, required: false, default: true },
  },
  data() {
    return {
      items: [{ id: "root", _type: "folder", name: "Home", children: [] }],
      open: [],
    };
  },
  computed: {
    computedValue: {
      get() {
        return [this.value];
      },
      set(v) {
        // eslint-disable-next-line no-underscore-dangle
        if (v[0] && v[0]._type === this.selectOnly) {
          console.debug(v[0], this.selectOnly);
          this.$emit("input", v[0]);
        }
      },
    },
    ...mapGetters({
      getDirectory: "directories/get",
      findDirectories: "directories/find",
    }),
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    if (!this.value) {
      this.$emit("input", { id: "root", name: "Home", _type: "folder" });
    }
    this.getRoot();
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      getDirectoryAction: "directories/get",
      findDirectoriesAction: "directories/find",
    }),
    async getRoot() {
      const root = this.findDirectories({ query: { id: "root" } }).data[0];
      if (!root) {
        const directories = await this.findDirectoriesAction({});
        this.updateItems(directories[0]);
      } else {
        this.updateItems(root);
      }
    },
    updateItems(directory) {
      this.items[0].children = this.parseChildren(directory);
      if (this.openedRoot) this.open.push(this.items[0]);
      this.openChildren();
    },
    getChildren(directory, { withOpen = true } = {}) {
      if (directory.id === "root") return;
      // Prefer store querying instead of db.
      // Avoid to update already loaded items and reset the tree
      const directoryObject = this.getDirectory(directory.id);
      if (directoryObject && directoryObject.cases) {
        directory.children.push(...this.parseChildren(directoryObject));
        if (withOpen) {
          this.open.push(directoryObject);
        }
      } else {
        // eslint-disable-next-line consistent-return
        this.getDirectoryAction(directory.id).then((r) => {
          directory.children.push(...this.parseChildren(r));
          if (directory.children.length || withOpen) {
            this.open.push(directory);
          }
        });
      }
    },
    getChildrenPromise(directory, { withOpen = true } = {}) {
      if (directory.id === "root") return false;
      // Prefer store querying instead of db.
      // Avoid to update already loaded items and reset the tree
      const directoryObject = this.getDirectory(directory.id);
      if (directoryObject && directoryObject.cases) {
        directory.children.push(...this.parseChildren(directoryObject));
        if (withOpen) {
          this.open.push(directoryObject);
        }
      } else {
        return this.getDirectoryAction(directory.id).then((r) => {
          directory.children.push(...this.parseChildren(r));
          if (directory.children.length || withOpen) {
            this.open.push(directory);
          }
        });
      }
      return false;
    },
    openChildren() {
      if (this.value.path) {
        const pathIds = this.value.path.split(".");

        // See https://stackoverflow.com/a/49499491/16360169
        pathIds.reduce(async (promise, id) => {
          // This line will wait for the last async function to finish.
          // The first iteration uses an already resolved Promise
          // so, it will immediately continue.
          await promise;
          const directory = this.treeSearch(id);
          if (directory) {
            await this.getChildrenPromise(directory, { withOpen: true });
          }
        }, Promise.resolve());
      }
    },
    parseChildren(parent) {
      return [
        ...parent.directories.map((v) => {
          return {
            ...v,
            _type: "folder",
            children: [],
          };
        }),
        ...(this.showCases ? parent.cases.map((v) => ({ ...v, _type: "case" })) : []),
      ];
    },
    treeSearch(id) {
      const collection = [this.items[0]];
      while (collection.length) {
        const parent = collection.shift();
        const directory = parent.children.find((child) => child.id === id);
        if (directory) {
          return directory;
        }
        collection.push(...parent.children);
      }
      return false;
    },
  },
};
</script>

<style>
.v-treeview-node__label {
  cursor: pointer;
}
</style>
